import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { WidgetInstance, localizations } from "friendly-challenge";

interface CaptchaError {
    code: string;
    description: string;
}

const friendlyCaptchaApiEndpoints = {
    eu: "https://eu-api.friendlycaptcha.eu/api/v1/puzzle",
    global: "https://api.friendlycaptcha.com/api/v1/puzzle",
};

@Component({
    tag: "ux-iws-captcha",
    styleUrl: "captcha.scss",
    shadow: false,
})
export class Captcha {
    private widget: WidgetInstance;
    private captchaElement: HTMLElement;
    private formElement: HTMLFormElement;
    private submitElement:
        | HTMLUxButtonElement
        | HTMLButtonElement
        | HTMLInputElement;

    @Element() element: HTMLUxIwsCaptchaElement;

    /** FriendlyCaptcha sitekey */
    @Prop() sitekey: string = "";

    /** FriendlyCaptcha widget language */
    @Prop() language: keyof typeof localizations = "en";

    /** FriendlyCaptcha puzzle endpoint (default: "eu") */
    @Prop() apiEndpoint: "eu" | "global" = "eu";

    /** FriendlyCaptcha widget start mode */
    @Prop() startMode: "auto" | "focus" | "none" = "auto";

    /** The form input name for the captcha solution */
    @Prop() solutionFieldName: string = "frc-captcha-solution";

    /** If set, form submit events are prevented until captcha verification is completed */
    @Prop() preventSubmit: boolean = false;

    @State() error: CaptchaError | null = null;
    @State() isVerified: boolean = false;

    @Watch("error")
    @Watch("isVerified")
    @Watch("preventSubmit")
    handleCaptchState() {
        if (this.isVerified || this.error) {
            this.disconnectForm();
        } else {
            if (this.submitElement) {
                this.submitElement.disabled = this.preventSubmit;
            }
        }
    }

    /** Emitted when FriendlyCaptcha has completed the verification */
    @Event({
        eventName: "uxCaptcha",
        composed: true,
        cancelable: false,
        bubbles: true,
    })
    captchaEvent: EventEmitter<{
        status: "ready" | "started" | "done" | "error";
        solution?: string;
        error?: CaptchaError;
    }>;

    private doneCallback = (solution: string) => {
        this.isVerified = true;
        this.error = null;
        if (this.submitElement) {
            this.submitElement.disabled = false;
        }
        this.captchaEvent.emit({ status: "done", solution });
    };

    private errorCallback = (error: CaptchaError) => {
        if (this.submitElement) {
            this.submitElement.disabled = false;
        }
        this.error = error;
        this.isVerified = false;
        this.captchaEvent.emit({
            status: "error",
            error,
        });
    };

    private readyCallback = () => {
        this.error = null;
        this.isVerified = false;
        if (this.submitElement) {
            this.submitElement.disabled = this.preventSubmit;
        }
        this.captchaEvent.emit({ status: "ready" });
    };

    private startedCallback = () => {
        this.error = null;
        this.isVerified = false;
        if (this.submitElement) {
            this.submitElement.disabled = this.preventSubmit;
        }
        this.captchaEvent.emit({ status: "started" });
    };

    private runWidget = () => {
        if (this.widget) {
            this.widget.destroy();
        }
        this.widget = new WidgetInstance(this.captchaElement, {
            doneCallback: this.doneCallback,
            errorCallback: this.errorCallback,
            readyCallback: this.readyCallback,
            startedCallback: this.startedCallback,
            puzzleEndpoint:
                friendlyCaptchaApiEndpoints[this.apiEndpoint] ||
                friendlyCaptchaApiEndpoints.global,
            sitekey: this.sitekey,
            startMode: this.startMode,
            language: this.language,
            solutionFieldName: this.solutionFieldName,
        });
    };

    private handleFormSubmitEvent = (event: Event) => {
        if (this.isVerified || !this.preventSubmit) {
            return;
        }
        event.preventDefault();
    };

    private handleSubmitClickEvent = (event: Event) => {
        if (this.isVerified || !this.preventSubmit) {
            if (this.submitElement) {
                this.submitElement.disabled = false;
            }
            return;
        }
        if (this.submitElement) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
    };

    private findSubmitElement = (
        within: HTMLElement
    ): HTMLUxButtonElement | HTMLButtonElement | HTMLInputElement => {
        return (
            within.querySelector(`ux-button[type="submit"]`) ||
            within.querySelector(`button[type="submit"]`) ||
            within.querySelector(`input[type="submit"]`)
        );
    };

    private disconnectForm = () => {
        if (this.submitElement) {
            this.submitElement.disabled = false;
            this.submitElement.removeEventListener(
                "click",
                this.handleSubmitClickEvent
            );
        }
        if (this.formElement) {
            this.formElement.removeEventListener(
                "submit",
                this.handleFormSubmitEvent
            );
            this.formElement.removeEventListener(
                "click",
                this.handleSubmitClickEvent
            );
        }
    };

    private connectForm = () => {
        this.disconnectForm();
        if (this.isVerified) {
            return;
        }
        this.formElement = this.element.closest("form");
        if (!(this.formElement instanceof HTMLFormElement)) {
            return;
        }
        this.formElement.addEventListener("submit", this.handleFormSubmitEvent);
        this.submitElement = this.findSubmitElement(this.formElement);
        if (!(this.submitElement instanceof HTMLElement)) {
            return;
        }
        this.submitElement.disabled = true;
        this.submitElement.addEventListener(
            "click",
            this.handleSubmitClickEvent
        );
    };

    componentWillLoad() {
        this.connectForm();
    }

    connectedCallback() {
        this.connectForm();
    }

    disconnectedCallback() {
        this.disconnectForm();
    }

    componentDidLoad() {
        this.runWidget();
    }

    render() {
        return (
            <Host>
                <div
                    class="frc-captcha"
                    ref={(el) => (this.captchaElement = el)}
                ></div>
            </Host>
        );
    }
}
